import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { HelpPanel } from '../components/ui/help-panel';
import { pdfStatementHelp } from '../lib/helpContent';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  Loader2,
  Sparkles,
  Building2,
  Calendar,
  DollarSign,
  FileSpreadsheet,
  Eye,
  Plus,
  Pencil,
  Trash2,
  Info,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const STEPS = [
  { id: 1, title: 'Select Account', description: 'Choose bank account and period' },
  { id: 2, title: 'Upload File', description: 'Upload OFX, QFX, CSV, or PDF' },
  { id: 3, title: 'Preview', description: 'Validate parsed transactions' },
  { id: 4, title: 'Match', description: 'Review automatic matches' },
  { id: 5, title: 'Complete', description: 'Approve and reconcile' },
];

export default function StatementUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Account selection
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('account') || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statementBalance, setStatementBalance] = useState('');
  
  // Step 2: File upload
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    date_column: '',
    description_column: '',
    amount_column: '',
    debit_column: '',
    credit_column: '',
    balance_column: '',
    reference_column: '',
    date_format: '%m/%d/%Y',
    skip_header_rows: 1,
  });
  const [usesSeparateColumns, setUsesSeparateColumns] = useState(false);
  
  // Step 3: Preview
  const [statementId, setStatementId] = useState(null);
  const [previewLines, setPreviewLines] = useState([]);
  const [previewWarnings, setPreviewWarnings] = useState([]);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [extractionConfidence, setExtractionConfidence] = useState(null);
  const [parsingNotes, setParsingNotes] = useState([]);
  
  // Manual line editing
  const [editingLine, setEditingLine] = useState(null);
  const [showAddLineDialog, setShowAddLineDialog] = useState(false);
  const [newLine, setNewLine] = useState({
    date: '',
    description: '',
    amount: '',
    amount_in: '',
    amount_out: '',
  });
  
  // Step 4: Matching
  const [matchResults, setMatchResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionCounts, setSuggestionCounts] = useState({});
  
  // Fetch bank accounts
  useEffect(() => {
    if (!currentWorkspace) return;
    
    const fetchAccounts = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/banking/accounts?workspace_id=${currentWorkspace.workspace_id}`
        );
        if (response.ok) {
          const data = await response.json();
          setBankAccounts(data.accounts || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch bank accounts:', error);
      }
    };
    
    fetchAccounts();
  }, [currentWorkspace]);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    const name = selectedFile.name.toLowerCase();
    if (name.endsWith('.ofx')) {
      setFileType('ofx');
      setFile(selectedFile);
    } else if (name.endsWith('.qfx')) {
      setFileType('qfx');
      setFile(selectedFile);
    } else if (name.endsWith('.csv')) {
      setFileType('csv');
      setFile(selectedFile);
      // Read CSV headers for mapping
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text === 'string') {
          const firstLine = text.split('\n')[0];
          const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
          setCsvHeaders(headers);
        }
      };
      reader.readAsText(selectedFile);
    } else if (name.endsWith('.pdf')) {
      setFileType('pdf');
      setFile(selectedFile);
    } else {
      toast.error('Unsupported file type. Please use OFX, QFX, CSV, or PDF.');
      setFile(null);
      setFileType(null);
    }
  };

  // Upload and parse file
  const handleUpload = async () => {
    if (!file || !selectedAccountId) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank_account_id', selectedAccountId);
      formData.append('workspace_id', currentWorkspace.workspace_id);
      if (startDate) formData.append('start_date', startDate);
      if (endDate) formData.append('end_date', endDate);
      if (statementBalance) formData.append('statement_balance', statementBalance);
      
      if (fileType === 'csv') {
        // Add CSV mapping
        const mapping = {
          date_column: csvMapping.date_column,
          description_column: csvMapping.description_column,
          date_format: csvMapping.date_format,
          skip_header_rows: csvMapping.skip_header_rows,
        };
        
        if (usesSeparateColumns) {
          mapping.debit_column = csvMapping.debit_column;
          mapping.credit_column = csvMapping.credit_column;
        } else {
          mapping.amount_column = csvMapping.amount_column;
        }
        
        if (csvMapping.balance_column && csvMapping.balance_column !== 'none') {
          mapping.balance_column = csvMapping.balance_column;
        }
        if (csvMapping.reference_column && csvMapping.reference_column !== 'none') {
          mapping.reference_column = csvMapping.reference_column;
        }
        
        formData.append('csv_mapping', JSON.stringify(mapping));
      }
      
      const response = await fetch(
        `${API_URL}/api/banking/statements/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }
      
      if (data.status === 'needs_mapping') {
        // CSV needs mapping - show headers
        setCsvHeaders(data.headers);
        toast.info('Please map the CSV columns to continue');
        return;
      }
      
      // Backwards-compat: older backend used to reject scanned PDFs outright
      // and return status="scanned_pdf". New backend routes scanned PDFs
      // through AI vision OCR, so this branch should no longer fire — but
      // keep it as a graceful fallback in case the OCR layer is unreachable.
      if (data.status === 'scanned_pdf') {
        toast.error(data.message || 'Could not process this scanned PDF');
        setPreviewWarnings(data.warnings || []);
        return;
      }
      
      // Handle partial extraction (PDF with low confidence)
      if (data.status === 'partial_extraction') {
        setStatementId(data.statement_id);
        setPreviewWarnings(data.warnings || []);
        setExtractionConfidence(data.metadata?.extraction_confidence);
        setParsingNotes(data.metadata?.parsing_notes || []);
        setPreviewLines([]);
        setCurrentStep(3);
        toast.warning('Could not extract transactions automatically. You can add lines manually.');
        return;
      }
      
      setStatementId(data.statement_id);
      setPreviewWarnings(data.warnings || []);
      setOverlapWarning(data.overlap_warning);
      setExtractionConfidence(data.extraction_confidence || data.metadata?.extraction_confidence);
      setParsingNotes(data.parsing_notes || data.metadata?.parsing_notes || []);
      
      // Auto-populate dates if provided by parser
      if (data.start_date && !startDate) setStartDate(data.start_date);
      if (data.end_date && !endDate) setEndDate(data.end_date);
      if (data.statement_balance && !statementBalance) {
        setStatementBalance(data.statement_balance.toString());
      }
      
      // Fetch preview
      await fetchPreview(data.statement_id);
      setCurrentStep(3);
      toast.success(`Uploaded ${data.line_count} transactions`);
      
    } catch (error) {
      toast.error(error.message || 'Failed to upload statement');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statement preview
  const fetchPreview = async (stmtId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${stmtId}/preview`
      );
      if (response.ok) {
        const data = await response.json();
        setPreviewLines(data.lines || []);
        setPreviewWarnings(data.warnings || []);
        if (data.extraction_confidence !== undefined) {
          setExtractionConfidence(data.extraction_confidence);
        }
        if (data.parsing_notes) {
          setParsingNotes(data.parsing_notes);
        }
      }
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };
  
  // Add new statement line
  const handleAddLine = async () => {
    if (!statementId || !newLine.date || !newLine.description) {
      toast.error('Date and description are required');
      return;
    }
    
    const amount = parseFloat(newLine.amount) || 0;
    const amountIn = parseFloat(newLine.amount_in) || null;
    const amountOut = parseFloat(newLine.amount_out) || null;
    
    if (amount === 0 && !amountIn && !amountOut) {
      toast.error('Please enter an amount');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/lines`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: newLine.date,
            description: newLine.description,
            amount: amount || (amountIn ? amountIn : -(amountOut || 0)),
            amount_in: amountIn,
            amount_out: amountOut,
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to add line');
      }
      
      toast.success('Line added');
      setShowAddLineDialog(false);
      setNewLine({ date: '', description: '', amount: '', amount_in: '', amount_out: '' });
      await fetchPreview(statementId);
    } catch (error) {
      toast.error(error.message || 'Failed to add line');
    } finally {
      setLoading(false);
    }
  };
  
  // Update statement line
  const handleUpdateLine = async (lineId, lineData) => {
    if (!statementId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/lines/${lineId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lineData),
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to update line');
      }
      
      toast.success('Line updated');
      setEditingLine(null);
      await fetchPreview(statementId);
    } catch (error) {
      toast.error(error.message || 'Failed to update line');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete statement line
  const handleDeleteLine = async (lineId) => {
    if (!statementId) return;
    
    if (!window.confirm('Are you sure you want to delete this line?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/lines/${lineId}`,
        {
          method: 'DELETE',
          }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete line');
      }
      
      toast.success('Line deleted');
      await fetchPreview(statementId);
    } catch (error) {
      toast.error(error.message || 'Failed to delete line');
    } finally {
      setLoading(false);
    }
  };

  // Run matching engine
  const handleRunMatching = async () => {
    if (!statementId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/match`,
        {
          method: 'POST',
          }
      );
      
      if (!response.ok) {
        throw new Error('Matching failed');
      }
      
      const data = await response.json();
      setMatchResults(data);
      
      // Fetch suggestions
      await fetchSuggestions();
      setCurrentStep(4);
      
      toast.success(`Matched ${data.matched_count} of ${data.total_lines} transactions`);
      
    } catch (error) {
      toast.error('Failed to run matching');
    } finally {
      setLoading(false);
    }
  };

  // Fetch suggestions
  const fetchSuggestions = async (tab = 'all') => {
    if (!statementId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/suggestions?tab=${tab}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setSuggestionCounts(data.counts || {});
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  // Approve suggestion
  const handleApproveSuggestion = async (suggestionId, action = null) => {
    try {
      const url = `${API_URL}/api/banking/statements/suggestions/${suggestionId}/approve${action ? `?action=${action}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        });
      
      if (response.ok) {
        toast.success('Approved');
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  // Bulk approve high confidence
  const handleBulkApprove = async () => {
    const highConfidenceIds = suggestions
      .filter(s => s.confidence_score >= 0.90 && s.status === 'pending')
      .map(s => s.suggestion_id);
    
    if (highConfidenceIds.length === 0) {
      toast.info('No high confidence suggestions to approve');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/suggestions/bulk-approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion_ids: highConfidenceIds }),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved_count} suggestions`);
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to bulk approve');
    } finally {
      setLoading(false);
    }
  };

  // Create the reconciliation from the statement, then navigate to it.
  const handleProceedToReconcile = async () => {
    if (!statementId) return;
    if (!currentWorkspace?.workspace_id) {
      toast.error('No workspace selected');
      return;
    }

    try {
      // 1. Get the reconcile-ready payload — confirms no pending suggestions
      //    AND returns the period + statement_balance we need to create the
      //    reconciliation row.
      const readyRes = await fetch(
        `${API_URL}/api/banking/statements/${statementId}/reconcile-ready`,
      );
      if (!readyRes.ok) {
        toast.error('Failed to prepare reconciliation');
        return;
      }
      const ready = await readyRes.json();

      if (ready.pending_suggestions_count > 0) {
        toast.warning(`${ready.pending_suggestions_count} suggestions still pending. Please review before reconciling.`);
        return;
      }

      // 2. Create the reconciliation row. Backend validates chronological
      //    order + checks for in-progress dupes. We surface those errors
      //    inline so the user can act on them instead of staring at a
      //    blank page (the original bug).
      const createRes = await fetch(
        `${API_URL}/api/banking/reconciliations?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bank_id: ready.bank_account_id || selectedAccountId,
            start_date: ready.start_date,
            end_date: ready.end_date,
            statement_balance: ready.statement_balance,
          }),
        },
      );

      if (!createRes.ok) {
        // The "in_progress" error payload includes the existing recon id —
        // route the user straight to it so they're never dead-ended.
        try {
          const err = await createRes.json();
          const detail = err?.detail || err;
          if (detail?.existing_reconciliation_id) {
            toast.info('Resuming the existing in-progress reconciliation for this account.');
            navigate(`/banking/reconcile/${detail.existing_reconciliation_id}`);
            return;
          }
          toast.error(detail?.message || detail || 'Failed to create reconciliation');
        } catch {
          toast.error('Failed to create reconciliation');
        }
        return;
      }

      const created = await createRes.json();
      const reconciliationId = created.reconciliation_id || created.id;
      if (!reconciliationId) {
        toast.error('Reconciliation created but no ID returned');
        return;
      }

      // 3. Pre-populate cleared / outstanding transaction IDs from the
      //    statement matches so the reconciliation page opens with the
      //    work already partially done.
      const cleared = ready.cleared_transaction_ids || [];
      const outstanding = ready.outstanding_transaction_ids || [];
      if (cleared.length > 0 || outstanding.length > 0) {
        try {
          await fetch(
            `${API_URL}/api/banking/reconciliations/${reconciliationId}?workspace_id=${currentWorkspace.workspace_id}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                cleared_transaction_ids: cleared,
                outstanding_transaction_ids: outstanding,
              }),
            },
          );
        } catch { /* non-fatal — user can still toggle them by hand */ }
      }

      // 4. Off to the actual reconciliation page (note: the route is
      //    `/banking/reconcile/:id`, NOT `/reconciliation/new`).
      navigate(`/banking/reconcile/${reconciliationId}`);
    } catch (error) {
      toast.error('Failed to prepare reconciliation');
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/banking/statements/csv-template`
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'statement_template.csv';
        a.click();
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const selectedAccount = bankAccounts.find(a => a.bank_id === selectedAccountId);

  return (
    <div className="container mx-auto p-6 max-w-5xl" data-testid="statement-upload-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/banking')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6" />
            Upload Bank Statement
          </h1>
          <p className="text-sm text-muted-foreground">
            Import transactions from your bank statement file
          </p>
        </div>
        {fileType === 'pdf' && <HelpPanel {...pdfStatementHelp} />}
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                    currentStep === step.id
                      ? "bg-primary text-primary-foreground"
                      : currentStep > step.id
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {currentStep > step.id ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    step.id
                  )}
                </div>
                <span className="text-xs mt-1 font-medium">{step.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2",
                    currentStep > step.id ? "bg-emerald-500" : "bg-muted"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Select Account */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-1-account">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank Account *</Label>
                  <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((account) => (
                        <SelectItem key={account.bank_id} value={account.bank_id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span>{account.name || account.official_name}</span>
                            {account.mask && (
                              <span className="text-muted-foreground">****{account.mask}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Statement Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      placeholder="YYYY-MM-DD"
                    />
                    <p className="text-xs text-muted-foreground">Optional - auto-detected from file</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Statement End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Ending Statement Balance</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      value={statementBalance}
                      onChange={(e) => setStatementBalance(e.target.value)}
                      placeholder="0.00"
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">The ending balance shown on your bank statement</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedAccountId}
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Upload File */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-2-upload">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  id="file-upload"
                  accept=".ofx,.qfx,.csv,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">
                    {file ? file.name : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Supported formats: OFX, QFX, CSV, PDF
                  </p>
                </label>
                
                {file && (
                  <Badge variant="outline" className="mt-4">
                    {fileType?.toUpperCase()} File Selected
                  </Badge>
                )}
              </div>
              
              {/* PDF Information */}
              {fileType === 'pdf' && (
                <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/20">
                  <Info className="h-4 w-4" />
                  <AlertTitle>PDF Statement Upload</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">Works best with digital PDFs downloaded directly from your bank.</p>
                    <p className="text-sm text-muted-foreground">
                      Scanned paper statements are also supported — we&apos;ll use AI vision to read the transactions. Review each line before finalizing. For 100%-accurate imports, CSV or OFX/QFX from your bank&apos;s online portal is recommended.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* CSV Mapping */}
              {fileType === 'csv' && csvHeaders.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      CSV Column Mapping
                    </CardTitle>
                    <CardDescription>
                      Map your CSV columns to the required fields
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date Column *</Label>
                        <Select
                          value={csvMapping.date_column}
                          onValueChange={(v) => setCsvMapping({ ...csvMapping, date_column: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            {csvHeaders.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Description Column *</Label>
                        <Select
                          value={csvMapping.description_column}
                          onValueChange={(v) => setCsvMapping({ ...csvMapping, description_column: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            {csvHeaders.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="separate-columns"
                          checked={usesSeparateColumns}
                          onChange={(e) => setUsesSeparateColumns(e.target.checked)}
                          className="rounded"
                        />
                        <Label htmlFor="separate-columns" className="font-normal">
                          Use separate Debit/Credit columns
                        </Label>
                      </div>
                    </div>

                    {usesSeparateColumns ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Debit Column *</Label>
                          <Select
                            value={csvMapping.debit_column}
                            onValueChange={(v) => setCsvMapping({ ...csvMapping, debit_column: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvHeaders.map((h) => (
                                <SelectItem key={h} value={h}>{h}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Credit Column *</Label>
                          <Select
                            value={csvMapping.credit_column}
                            onValueChange={(v) => setCsvMapping({ ...csvMapping, credit_column: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvHeaders.map((h) => (
                                <SelectItem key={h} value={h}>{h}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>Amount Column *</Label>
                        <Select
                          value={csvMapping.amount_column}
                          onValueChange={(v) => setCsvMapping({ ...csvMapping, amount_column: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            {csvHeaders.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Negative values = money out, Positive = money in
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Balance Column (optional)</Label>
                        <Select
                          value={csvMapping.balance_column}
                          onValueChange={(v) => setCsvMapping({ ...csvMapping, balance_column: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {csvHeaders.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Date Format</Label>
                        <Select
                          value={csvMapping.date_format}
                          onValueChange={(v) => setCsvMapping({ ...csvMapping, date_format: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="%m/%d/%Y">MM/DD/YYYY</SelectItem>
                            <SelectItem value="%d/%m/%Y">DD/MM/YYYY</SelectItem>
                            <SelectItem value="%Y-%m-%d">YYYY-MM-DD</SelectItem>
                            <SelectItem value="%m-%d-%Y">MM-DD-YYYY</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1 hover:text-foreground"
                >
                  <Download className="w-4 h-4" />
                  Download CSV Template
                </button>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!file || loading || (fileType === 'csv' && (!csvMapping.date_column || !csvMapping.description_column))}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Upload & Parse
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-3-preview">
              {/* PDF Extraction Confidence Warning */}
              {fileType === 'pdf' && extractionConfidence !== null && extractionConfidence < 0.8 && (
                <Alert variant="warning" className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertTitle>Low Extraction Confidence</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">
                      AI Buck extracted data with {Math.round(extractionConfidence * 100)}% confidence. 
                      Please review and edit lines before proceeding.
                    </p>
                    <div className="mt-3 p-3 bg-white/50 rounded border border-amber-100">
                      <p className="font-medium text-sm mb-2">Common Issues Checklist:</p>
                      <ul className="list-disc list-inside text-sm space-y-1 text-amber-800">
                        <li>Missing dates — some rows may need dates filled in</li>
                        <li>Merged rows — multi-line descriptions may be combined</li>
                        <li>Negative sign issues — verify debits are negative</li>
                        <li>Totals included — delete any summary/total rows</li>
                      </ul>
                    </div>
                    {parsingNotes.length > 0 && (
                      <div className="mt-2 text-xs text-amber-700">
                        <p className="font-medium">Parser Notes:</p>
                        <ul className="list-disc list-inside">
                          {parsingNotes.map((note, i) => (
                            <li key={i}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Empty state - allow manual entry */}
              {previewLines.length === 0 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="w-4 h-4" />
                  <AlertTitle>No Transactions Extracted</AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">
                      Could not automatically extract transactions from this file. You can add lines manually below.
                    </p>
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddLineDialog(true)}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Transaction
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Warnings */}
              {(previewWarnings.length > 0 || overlapWarning) && (
                <Alert variant="warning">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertTitle>Warnings</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      {overlapWarning && (
                        <li className="text-amber-700">{overlapWarning}</li>
                      )}
                      {previewWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-2xl font-bold">{previewLines.length}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(previewLines.filter(l => l.amount < 0).reduce((s, l) => s + Math.abs(l.amount), 0))}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Out</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(previewLines.filter(l => l.amount > 0).reduce((s, l) => s + l.amount, 0))}
                  </p>
                  <p className="text-xs text-muted-foreground">Total In</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-2xl font-bold">
                    {previewLines.filter(l => l.has_warning).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Warnings</p>
                </div>
              </div>
              
              {/* Manual Line Controls */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Review and edit transactions before matching
                </p>
                <div className="flex items-center gap-2">
                  {fileType === 'pdf' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setCurrentStep(2);
                        setFile(null);
                        setFileType(null);
                      }}
                    >
                      Switch File Type
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddLineDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Line
                  </Button>
                </div>
              </div>

              {/* Transaction Table */}
              <ScrollArea className="h-[400px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right w-[120px]">Amount Out</TableHead>
                      <TableHead className="text-right w-[120px]">Amount In</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewLines.map((line) => (
                      <TableRow key={line.line_id} className={line.has_warning ? 'bg-amber-50/50' : ''}>
                        <TableCell className="font-mono text-sm">{line.date}</TableCell>
                        <TableCell>
                          <div>
                            <span className="truncate block max-w-[300px]">{line.description}</span>
                            {line.has_warning && (
                              <span className="text-xs text-amber-600">{line.warning_message}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          {line.amount_out ? formatCurrency(line.amount_out) : (line.amount < 0 ? formatCurrency(Math.abs(line.amount)) : '—')}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {line.amount_in ? formatCurrency(line.amount_in) : (line.amount > 0 ? formatCurrency(line.amount) : '—')}
                        </TableCell>
                        <TableCell>
                          {line.has_warning ? (
                            <Badge variant="outline" className="text-amber-600">Warning</Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600">Valid</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditingLine(line)}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => handleDeleteLine(line.line_id)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleRunMatching} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Run Auto-Matching
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Match Results */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-4-match">
              {/* Match Summary */}
              {matchResults && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{suggestionCounts.high_confidence || 0}</p>
                    <p className="text-xs text-muted-foreground">High Confidence</p>
                  </div>
                  <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{suggestionCounts.needs_review || 0}</p>
                    <p className="text-xs text-muted-foreground">Needs Review</p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{suggestionCounts.unmatched || 0}</p>
                    <p className="text-xs text-muted-foreground">Unmatched</p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{suggestionCounts.approved || 0}</p>
                    <p className="text-xs text-muted-foreground">Approved</p>
                  </div>
                </div>
              )}

              {/* Bulk Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkApprove}
                  disabled={loading}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Approve All High Confidence
                </Button>
              </div>

              {/* Suggestions Table */}
              <ScrollArea className="h-[400px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Score</TableHead>
                      <TableHead>Statement Line</TableHead>
                      <TableHead>Matched To</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suggestions.map((sugg) => (
                      <TableRow key={sugg.suggestion_id}>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              sugg.confidence_score >= 0.90
                                ? "bg-emerald-100 text-emerald-700"
                                : sugg.confidence_score >= 0.60
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                            )}
                          >
                            {Math.round(sugg.confidence_score * 100)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{sugg.statement_description}</p>
                            <p className="text-xs text-muted-foreground">
                              {sugg.statement_date} • {formatCurrency(sugg.statement_amount)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sugg.matched_id ? (
                            <div>
                              <p className="truncate max-w-[200px]">{sugg.matched_description}</p>
                              <p className="text-xs text-muted-foreground">
                                {sugg.matched_date} • {formatCurrency(sugg.matched_amount)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic">No match found</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {sugg.suggested_action?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {sugg.status === 'approved' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>
                          ) : sugg.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-700">Rejected</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {sugg.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleApproveSuggestion(sugg.suggestion_id)}
                            >
                              Approve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleProceedToReconcile}>
                  Proceed to Reconciliation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add Line Dialog */}
      <Dialog open={showAddLineDialog} onOpenChange={setShowAddLineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Statement Line</DialogTitle>
            <DialogDescription>
              Manually add a transaction line to the statement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newLine.date}
                  onChange={(e) => setNewLine({ ...newLine, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Negative for outflow"
                  value={newLine.amount}
                  onChange={(e) => setNewLine({ ...newLine, amount: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={newLine.description}
                onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                placeholder="Transaction description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount Out (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newLine.amount_out}
                  onChange={(e) => setNewLine({ ...newLine, amount_out: e.target.value })}
                  placeholder="Debit amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount In (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newLine.amount_in}
                  onChange={(e) => setNewLine({ ...newLine, amount_in: e.target.value })}
                  placeholder="Credit amount"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLineDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLine} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Line Dialog */}
      <Dialog open={!!editingLine} onOpenChange={() => setEditingLine(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Statement Line</DialogTitle>
            <DialogDescription>
              Update the transaction details
            </DialogDescription>
          </DialogHeader>
          {editingLine && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    defaultValue={editingLine.date}
                    id="edit-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    defaultValue={editingLine.amount}
                    id="edit-amount"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  defaultValue={editingLine.description}
                  id="edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount Out</Label>
                  <Input
                    type="number"
                    step="0.01"
                    defaultValue={editingLine.amount_out || ''}
                    id="edit-amount-out"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount In</Label>
                  <Input
                    type="number"
                    step="0.01"
                    defaultValue={editingLine.amount_in || ''}
                    id="edit-amount-in"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLine(null)}>
              Cancel
            </Button>
            <Button 
              disabled={loading}
              onClick={() => {
                const dateEl = document.getElementById('edit-date');
                const amountEl = document.getElementById('edit-amount');
                const descEl = document.getElementById('edit-description');
                const amountOutEl = document.getElementById('edit-amount-out');
                const amountInEl = document.getElementById('edit-amount-in');
                
                handleUpdateLine(editingLine.line_id, {
                  date: dateEl?.value || editingLine.date,
                  amount: parseFloat(amountEl?.value) || editingLine.amount,
                  description: descEl?.value || editingLine.description,
                  amount_out: parseFloat(amountOutEl?.value) || null,
                  amount_in: parseFloat(amountInEl?.value) || null,
                });
              }}
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
